/**
 * Single source of truth for "may this viewer write to a task on this project?".
 *
 * The server is the authority — these predicates exist so the dashboard stops
 * lying to the user about what a click will do, not to enforce anything.
 *
 * Today the API scopes every to-do write to the task's creator
 * (`to_do.user_id === caller` in `validateUpdatingToDo`), while the project task
 * list is read by `project_id` with no user filter. A member therefore sees every
 * card on a shared board and is refused on every card they did not create — and
 * the refusal is a 401, which the axios interceptor treats as an expired session.
 *
 * Focus-Bear/backend#2413 changes that rule to project membership. When it ships,
 * flip {@link PROJECT_TASK_WRITE_SCOPE} to `ProjectTaskWriteScope.PROJECT_MEMBER`
 * and every surface below follows — that constant is the only edit required.
 */
import { isAxiosError } from 'axios';
import { ProjectTaskWriteScope } from 'enums';
import { PROJECT_TASK_WRITE_SCOPE } from 'constants/project';
import { HTTP_STATS_CODE } from 'constants/general';
import {
  Project,
  ProjectMemberInvitationStatus,
  ProjectMemberRole
} from 'interfaces/project.interface';
import { getViewerProjectRole } from 'utils/projects/project.util';

export interface ProjectTaskPermissions {
  canAddTask: boolean;
  canEditTask: boolean;
  canMoveTask: boolean;
  canDeleteTask: boolean;
  /**
   * `false` while the server still scopes writes to the task's creator: the
   * viewer may attempt a write, but the API can refuse it for someone else's card.
   */
  isTaskWriteGuaranteed: boolean;
  /**
   * Show the up-front explanation of the owner-scoped rule. Only meaningful on a
   * shared project — a solo project never hits the restriction.
   */
  shouldExplainOwnerScopedWrites: boolean;
}

type ProjectMembership = Pick<Project, 'members' | 'owner_id'>;

const isProjectMember = (role?: ProjectMemberRole): boolean =>
  role === ProjectMemberRole.OWNER ||
  role === ProjectMemberRole.ADMIN ||
  role === ProjectMemberRole.MEMBER;

const canManageProjectTasks = (role?: ProjectMemberRole): boolean =>
  role === ProjectMemberRole.OWNER || role === ProjectMemberRole.ADMIN;

/** A project is "shared" once someone other than the viewer has accepted an invite. */
export const hasOtherAcceptedMembers = (
  project: ProjectMembership,
  viewerId?: string
): boolean =>
  (project.members ?? []).some(
    (member) =>
      member.invitation_status === ProjectMemberInvitationStatus.ACCEPTED &&
      Boolean(member.user_id) &&
      member.user_id !== viewerId
  );

export const getProjectTaskPermissions = ({
  project,
  viewerId,
  writeScope = PROJECT_TASK_WRITE_SCOPE
}: {
  project: ProjectMembership;
  viewerId?: string;
  writeScope?: ProjectTaskWriteScope;
}): ProjectTaskPermissions => {
  const viewerRole = getViewerProjectRole(project, viewerId);
  const isMember = isProjectMember(viewerRole);
  const isOwnerScoped = writeScope === ProjectTaskWriteScope.TASK_OWNER;

  return {
    canAddTask: isMember,
    // A member can always write the tasks they created, so gating edit/move by
    // role would lock them out of their own cards — the bug this replaces.
    canEditTask: isMember,
    canMoveTask: isMember,
    // Deleting someone else's card is destructive and out of scope for #2413's
    // report, so it stays with the people who administer the project.
    canDeleteTask: canManageProjectTasks(viewerRole),
    isTaskWriteGuaranteed: isMember && !isOwnerScoped,
    shouldExplainOwnerScopedWrites:
      isMember && isOwnerScoped && hasOtherAcceptedMembers(project, viewerId)
  };
};

/**
 * `validateUpdatingToDo` answers "not your to-do" with an `UnauthorizedException`
 * (401); 403 is covered too in case the server change reclassifies it. While the
 * rule is owner-scoped, a refused task write means the card belongs to someone
 * else — say so instead of a generic "could not update".
 */
const TASK_WRITE_REFUSAL_STATUSES: readonly number[] = [
  HTTP_STATS_CODE.UNAUTHORIZED,
  HTTP_STATS_CODE.FORBIDDEN
];

export const isOwnerScopedTaskWriteRefusal = (
  error: unknown,
  writeScope: ProjectTaskWriteScope = PROJECT_TASK_WRITE_SCOPE
): boolean => {
  if (writeScope !== ProjectTaskWriteScope.TASK_OWNER || !isAxiosError(error)) {
    return false;
  }
  const status = error.response?.status;
  return status != null && TASK_WRITE_REFUSAL_STATUSES.includes(status);
};
